'use client';

// ACTAS DE REPARTO en dos modos (menú Instrucción → Actas de reparto):
//  - «Ver» (modo 'ver'): lo pendiente de CADA rol (el sustanciador aprueba y firma,
//    el director aprueba y firma, el técnico ve sus devueltas y las firmadas por
//    asignar) + FILTROS POR ESTADO (chips con conteo). Para el TÉCNICO incluye
//    además sus solicitudes EN DILIGENCIAMIENTO (la antigua vista «Expedientes no
//    iniciados», cuyo ítem de menú se eliminó): botón «Continuar» retoma la
//    pre-radicación en el Libro radicador.
//  - «Reportes» (modo 'reportes'): HISTORIAL de todas las actas (incluye las que
//    cada quien ya firmó), solo consulta.
// Ambos comen de GET /solicitudes/actas/todas (+ GET /solicitudes EN_PROCESO
// para el técnico en «Ver»).

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight, ArrowRightCircle, Eye, FileSignature, PencilLine, RefreshCw, RotateCcw,
} from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import type { Sesion } from '@/lib/auth';
import {
  aprobarActa, devolverActa, firmarActa, listarActasTodas, listarFirmantes,
  listarSolicitudes, labelCanal, ESTADO_ACTA_LABEL,
  type EstadoActa, type Firmante, type Solicitud,
} from '@/lib/preRadicacion';
import { esAbogadoSustanciador } from '@/lib/roles';
import ActaRepartoDialog from '@/components/ActaRepartoDialog';
import HeroVista from '@/components/HeroVista';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const fechaCorta = (iso?: string | null) => (iso ? iso.slice(0, 10) : '—');

// Filtros por estado de la vista «Ver». 'pendientes' = lo que le toca al rol.
// 'EN_DILIGENCIAMIENTO' = solicitudes EN_PROCESO sin acta enviada (solo técnico).
type Filtro =
  | 'pendientes'
  | 'EN_DILIGENCIAMIENTO'
  | 'PENDIENTE_SUSTANCIADOR'
  | 'PENDIENTE_DIRECTOR'
  | 'DEVUELTA_TECNICO'
  | 'FIRMADA_SIN_ASIGNAR'
  | 'todas';

const FILTROS: Array<{ v: Filtro; label: string }> = [
  { v: 'pendientes', label: 'Mis pendientes' },
  { v: 'EN_DILIGENCIAMIENTO', label: 'En diligenciamiento' },
  { v: 'PENDIENTE_SUSTANCIADOR', label: 'Pendiente sustanciador' },
  { v: 'PENDIENTE_DIRECTOR', label: 'Pendiente director' },
  { v: 'DEVUELTA_TECNICO', label: 'Devueltas' },
  { v: 'FIRMADA_SIN_ASIGNAR', label: 'Firmada, falta asignación' },
  { v: 'todas', label: 'Todas' },
];

function colorEstado(e?: EstadoActa | null): string {
  if (e === 'FIRMADA') return 'border-primary/40 bg-primary/5 text-primary';
  if (e === 'DEVUELTA_TECNICO') return 'border-destructive/40 bg-destructive/5 text-destructive';
  return 'border-amber-400/60 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300';
}

export default function BandejaActas({
  usuario,
  modo = 'ver',
  onNavegar,
}: {
  usuario: Sesion;
  modo?: 'ver' | 'reportes';
  onNavegar?: (vista: string) => void;
}) {
  const [filas, setFilas] = useState<Solicitud[]>([]);
  const [firmantes, setFirmantes] = useState<Firmante[]>([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState<Filtro>(modo === 'reportes' ? 'todas' : 'pendientes');
  const [verActa, setVerActa] = useState<Solicitud | null>(null);
  const [devolviendo, setDevolviendo] = useState<Solicitud | null>(null);
  const [procesando, setProcesando] = useState<string | null>(null); // id en curso

  const esAdmin = usuario.rol === 'ADMINISTRADOR';
  const esSustanciador = esAbogadoSustanciador(usuario.rol) || esAdmin;
  const esDirector = usuario.rol === 'DIRECTOR_INSTRUCCION' || esAdmin;
  const esTecnico = usuario.rol === 'TECNICO_ADMINISTRATIVO'
    || usuario.rol === 'APOYO_TECNICO_ADMINISTRATIVO' || esAdmin;

  async function cargar() {
    setCargando(true);
    try {
      const actas = await listarActasTodas();
      // «Ver» del técnico: se suman sus solicitudes EN_PROCESO sin acta enviada
      // (lo que antes cargaba la vista «Expedientes no iniciados», hoy sin menú).
      let enProceso: Solicitud[] = [];
      if (modo === 'ver' && esTecnico) {
        try { enProceso = await listarSolicitudes('EN_PROCESO'); } catch { /* sin permiso */ }
      }
      const conActa = new Set(actas.map((s) => s.id));
      setFilas([...enProceso.filter((s) => !conActa.has(s.id)), ...actas]);
    } catch (e) {
      avisoError('No se pudieron cargar las actas', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    listarFirmantes().then(setFirmantes).catch(() => setFirmantes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ¿Esta acta está pendiente DE MÍ? (según el rol y, para el sustanciador, si
  // el acta le fue enviada a él o quedó "para cualquiera").
  function pendienteDeMi(s: Solicitud): boolean {
    if (esSustanciador && s.acta_estado === 'PENDIENTE_SUSTANCIADOR'
      && (!s.sustanciador_id || s.sustanciador_id === usuario.id || esAdmin)) return true;
    if (esDirector && s.acta_estado === 'PENDIENTE_DIRECTOR') return true;
    if (esTecnico && s.acta_estado === 'DEVUELTA_TECNICO') return true;
    if (esTecnico && s.acta_estado === 'FIRMADA' && !s.asignacion_enviada) return true;
    if (esTecnico && !s.acta_estado) return true; // en diligenciamiento (sin acta enviada)
    return false;
  }

  function pasaFiltro(s: Solicitud, f: Filtro): boolean {
    switch (f) {
      case 'pendientes': return pendienteDeMi(s);
      case 'EN_DILIGENCIAMIENTO': return !s.acta_estado;
      case 'FIRMADA_SIN_ASIGNAR': return s.acta_estado === 'FIRMADA' && !s.asignacion_enviada;
      case 'todas': return true;
      default: return s.acta_estado === f;
    }
  }

  const visibles = useMemo(
    () => (modo === 'reportes' ? filas : filas.filter((s) => pasaFiltro(s, filtro))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filas, filtro, modo],
  );

  const conteo = (f: Filtro) => filas.filter((s) => pasaFiltro(s, f)).length;

  // El chip «En diligenciamiento» solo aplica al técnico (es SU trabajo sin terminar).
  const filtrosVisibles = FILTROS.filter((f) => f.v !== 'EN_DILIGENCIAMIENTO' || esTecnico);

  // Retomar una solicitud sin terminar (o devuelta): mismo handoff que usaba la
  // vista «Expedientes no iniciados» — el Libro radicador la abre para continuarla.
  function continuar(s: Solicitud) {
    localStorage.setItem('solicitudPendiente', JSON.stringify({ id: s.id }));
    onNavegar?.('radicar-queja');
  }

  // El sustanciador APRUEBA Y FIRMA (da su aprobación; el acta pasa al director).
  async function aprobarSustanciador(s: Solicitud) {
    setProcesando(s.id);
    try {
      await aprobarActa(s.id);
      avisoExito('Acta aprobada y firmada: pasó al Director de Instrucción.');
      cargar();
    } catch (e) {
      avisoError('No se pudo aprobar el acta', e instanceof Error ? e.message : undefined);
    } finally {
      setProcesando(null);
    }
  }

  async function firmarDirector(s: Solicitud) {
    setProcesando(s.id);
    try {
      await firmarActa(s.id);
      avisoExito('Acta aprobada y FIRMADA.', 'El expediente pasó a «Expedientes aprobados» del técnico administrativo.');
      cargar();
    } catch (e) {
      avisoError('No se pudo firmar el acta', e instanceof Error ? e.message : undefined);
    } finally {
      setProcesando(null);
    }
  }

  const esReporte = modo === 'reportes';

  return (
    <div className="space-y-6">
      <HeroVista
        icono={FileSignature}
        titulo={esReporte ? 'Actas de reparto — Reportes' : 'Actas de reparto'}
        descripcion={esReporte
          ? 'Historial de todas las actas de reparto: en revisión, devueltas, firmadas y asignadas.'
          : 'Pendientes de cada rol con filtros por estado: el sustanciador y el Director aprueban y firman; el técnico continúa las que están en diligenciamiento, corrige devueltas y asigna las firmadas.'}
        acciones={
          <Button
            variant="outline"
            className="border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20"
            onClick={cargar}
          >
            <RefreshCw className="size-4" /> Actualizar
          </Button>
        }
      />

      {/* Filtros por estado (solo en «Ver»). */}
      {!esReporte && (
        <div className="flex flex-wrap gap-2">
          {filtrosVisibles.map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFiltro(f.v)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                filtro === f.v
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground',
              )}
            >
              {f.label} · {conteo(f.v)}
            </button>
          ))}
        </div>
      )}

      {cargando ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Cargando…</p>
      ) : visibles.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {esReporte ? 'Aún no hay actas de reparto.' : 'No hay actas en este filtro.'}
        </p>
      ) : (
        <div className="space-y-4">
          {visibles.map((s) => {
            const puedeSustanciador = !esReporte && esSustanciador
              && s.acta_estado === 'PENDIENTE_SUSTANCIADOR'
              && (!s.sustanciador_id || s.sustanciador_id === usuario.id || esAdmin);
            const puedeDirector = !esReporte && esDirector && s.acta_estado === 'PENDIENTE_DIRECTOR';
            const puedeCorregir = !esReporte && esTecnico && s.acta_estado === 'DEVUELTA_TECNICO';
            const puedeAsignar = !esReporte && esTecnico
              && s.acta_estado === 'FIRMADA' && !s.asignacion_enviada;
            const puedeContinuar = !esReporte && esTecnico && !s.acta_estado;
            return (
              <div key={s.id} className="flex flex-col gap-3 rounded-2xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-primary">
                      {s.radicado_completo ?? s.radicado_origen ?? 'Sin radicado'}
                    </p>
                    <Badge
                      variant="outline"
                      className={colorEstado(s.acta_estado)}
                      title={!s.acta_estado && !s.completa ? s.faltantes?.join(' · ') : undefined}
                    >
                      {s.acta_estado
                        ? ESTADO_ACTA_LABEL[s.acta_estado]
                        : s.completa
                          ? 'Pendiente por contratista'
                          : `En diligenciamiento · ${s.faltantes?.length ?? 0} pendiente(s)`}
                    </Badge>
                    {s.acta_estado === 'FIRMADA' && (
                      <Badge variant="outline" className={s.asignacion_enviada
                        ? 'border-primary/40 bg-primary/5 text-primary'
                        : 'border-amber-400/60 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300'}>
                        {s.asignacion_enviada ? 'Proceso asignado' : 'Falta la asignación'}
                      </Badge>
                    )}
                    {/* El director la devolvió: volvió al sustanciador para reiniciar el flujo. */}
                    {s.acta_estado === 'PENDIENTE_SUSTANCIADOR' && s.acta_observacion && (
                      <Badge variant="outline" className="border-destructive/40 bg-destructive/5 text-destructive">
                        Devuelta por el director
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Ingreso: {fechaCorta(s.fecha_ingreso)} · Canal: {labelCanal(s.canal)}
                    {s.tecnico_nombre && <> · Elaboró: {s.tecnico_nombre}</>}
                    {s.sustanciador_nombre && <> · Sustanciador: {s.sustanciador_nombre}</>}
                    {s.director_nombre && <> · Director: {s.director_nombre}</>}
                  </p>
                  {esReporte && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {s.acta_visto_bueno_en && <>Aprobada por el sustanciador: {fechaCorta(s.acta_visto_bueno_en)} · </>}
                      {s.acta_firmada_en && <>Firmada por el director: {fechaCorta(s.acta_firmada_en)}</>}
                    </p>
                  )}
                  {(s.acta_estado === 'DEVUELTA_TECNICO' || s.acta_estado === 'PENDIENTE_SUSTANCIADOR')
                    && s.acta_observacion && (
                    <p className="mt-0.5 text-xs text-destructive/90">Observación: {s.acta_observacion}</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {s.acta_estado && (
                    <Button variant="outline" onClick={() => setVerActa(s)}>
                      <Eye className="size-4" /> Ver acta
                    </Button>
                  )}
                  {puedeContinuar && (
                    <Button onClick={() => continuar(s)}>
                      <ArrowRightCircle className="size-4" /> Continuar
                    </Button>
                  )}
                  {puedeSustanciador && (
                    <Button disabled={procesando === s.id} onClick={() => aprobarSustanciador(s)}>
                      <FileSignature className="size-4" /> Aprobar y firmar
                    </Button>
                  )}
                  {puedeDirector && (
                    <Button disabled={procesando === s.id} onClick={() => firmarDirector(s)}>
                      <FileSignature className="size-4" /> Aprobar y firmar
                    </Button>
                  )}
                  {puedeCorregir && (
                    <Button variant="outline" onClick={() => continuar(s)}>
                      <PencilLine className="size-4" /> Corregir
                    </Button>
                  )}
                  {puedeAsignar && (
                    <Button variant="outline" onClick={() => onNavegar?.('casos-aprobados')}>
                      <ArrowRight className="size-4" /> Asignar proceso
                    </Button>
                  )}
                  {(puedeSustanciador || puedeDirector) && (
                    <Button
                      variant="outline"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      disabled={procesando === s.id}
                      onClick={() => setDevolviendo(s)}
                    >
                      <RotateCcw className="size-4" /> Devolver
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {verActa && (
        <ActaRepartoDialog solicitud={verActa} firmantes={firmantes} onClose={() => setVerActa(null)} />
      )}

      {devolviendo && (
        <ModalDevolver
          solicitud={devolviendo}
          onClose={() => setDevolviendo(null)}
          onListo={() => { setDevolviendo(null); cargar(); }}
        />
      )}
    </div>
  );
}

// Devolución del acta al técnico con observación (obligatoria).
function ModalDevolver({
  solicitud, onClose, onListo,
}: {
  solicitud: Solicitud;
  onClose: () => void;
  onListo: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function devolver(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await devolverActa(solicitud.id, motivo.trim());
      avisoExito('Acta devuelta al técnico administrativo con su observación.');
      onListo();
    } catch (err) {
      avisoError('No se pudo devolver el acta', err instanceof Error ? err.message : undefined);
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="size-5 text-destructive" /> Devolver acta de reparto
          </DialogTitle>
          <DialogDescription>
            Expediente {solicitud.radicado_completo ?? solicitud.radicado_origen ?? ''}: el acta vuelve
            al técnico administrativo para corregir.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={devolver} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="motivo">Observación</Label>
            <Textarea
              id="motivo" rows={3} required minLength={3}
              value={motivo} onChange={(e) => setMotivo(e.target.value)}
              placeholder="¿Qué se debe corregir?"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="destructive" disabled={guardando || motivo.trim().length < 3}>
              {guardando ? 'Devolviendo…' : 'Devolver'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
